import {
  AutoInvoiceServiceType,
  AutoInvoiceSetting,
  EditAutoInvoiceSetting,
} from "@/models/api/administration/contractors/AutoInvoiceSetting";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import {
  addContractorAutoInvoiceSetting,
  editAutoInvoiceConfigurationName,
  editAutoInvoiceConfigurationServiceTypes,
  editAutoInvoiceSetting,
  getAllServiceTypesAutoInvoiceConfigurations,
  getContractorAutoInvoiceSettings,
  getFilteredServiceTypesAutoInvoiceConfigurations,
  getServiceTypesAutoInvoiceConfigurations,
  removeAutoInvoiceConfiguration,
} from "@/requests/administration/contractors/contractorAutoInvoiceAdministration";
import { FILTER_NAMES } from "@/static/filterNames";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { defineModule } from "direct-vuex";
import { DataOptions } from "vuetify";

export interface AdministrationContractorAutoInvoiceInterface {
  isAddDialog: boolean;
  isEditDialog: boolean;
  recordStampId: number | null;
  autoInvoiceSettings: Array<AutoInvoiceSetting>;
  autoInvoiceSettingId: number;
  autoInvoiceSettingName: string;

  autoInvoiceConfigurationId: number | null;
  serviceTypesFilters: null | Array<CriterionBase>;
  serviceTypesCount: number;
  serviceTypeDataTableOptions: DataOptions;
  areSelectedServicesLoading: boolean;
  areUnusedServicesLoading: boolean;
  selectedServices: Array<AutoInvoiceServiceType>;
  unusedServices: Array<AutoInvoiceServiceType>;
}

export const AdministrationContractorAutoInvoiceState: AdministrationContractorAutoInvoiceInterface = {
  isAddDialog: false,
  isEditDialog: false,
  recordStampId: null,
  autoInvoiceSettings: [],
  autoInvoiceSettingId: 0,
  autoInvoiceSettingName: "",

  autoInvoiceConfigurationId: null,
  serviceTypesFilters: null,
  serviceTypesCount: -1,
  serviceTypeDataTableOptions: new DefaultDataOptions(),
  areSelectedServicesLoading: false,
  areUnusedServicesLoading: false,
  selectedServices: [],
  unusedServices: [],
};

const AdministrationContractorAutoInvoiceModule = defineModule({
  state: AdministrationContractorAutoInvoiceState as AdministrationContractorAutoInvoiceInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsAddDialog(state, param: boolean) {
      state.isAddDialog = param;
    },
    setAutoInvoiceSettings(state, param: Array<AutoInvoiceSetting>) {
      state.autoInvoiceSettings = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    openEditAutoInvoiceSettingName(state, param: { id: number; name: string }) {
      state.autoInvoiceSettingId = param.id;
      state.autoInvoiceSettingName = param.name;
      state.isEditDialog = true;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setAutoInvoiceConfigurationId(state, param: number | null) {
      state.autoInvoiceConfigurationId = param;
    },
    setAreSelectedServicesLoading(state, param: boolean) {
      state.areSelectedServicesLoading = param;
    },
    setAreUnusedServicesLoading(state, param: boolean) {
      state.areUnusedServicesLoading = param;
    },
    setServiceTypesFilters(state, param: Array<CriterionBase> | null) {
      state.serviceTypesFilters = param;
    },
    setServiceDataTableOptions(state, param: DataOptions) {
      state.serviceTypeDataTableOptions = param;
    },
    setServiceTypesCount(state, param: number) {
      state.serviceTypesCount = param;
    },
    setSelectedService(state, param: Array<AutoInvoiceServiceType>) {
      state.selectedServices = param;
    },
    setUnusedService(state, param: Array<AutoInvoiceServiceType>) {
      state.unusedServices = param;
    },
  },
  actions: {
    async addAutoInvoiceSetting(context, param: string): Promise<number> {
      const { rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        return await addContractorAutoInvoiceSetting(
          rootState.administration.contractors.list.selectedContractorId,
          param
        );
      }
      return 0;
    },
    async editAutoInvoiceSetting(context, param: { id: number; autoInvoiceSetting: EditAutoInvoiceSetting }) {
      const { dispatch } = _moduleActionContext(context);
      await editAutoInvoiceSetting(param.id, param.autoInvoiceSetting);
      dispatch.downloadAutoInvoiceSetting();
    },
    async editAutoInvoiceSettingName(context, param: string) {
      const { dispatch, state } = _moduleActionContext(context);
      await editAutoInvoiceConfigurationName(state.autoInvoiceSettingId as number, param);
      dispatch.downloadAutoInvoiceSetting();
    },
    async downloadAutoInvoiceSetting(context) {
      const { rootState, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const result = await getContractorAutoInvoiceSettings(
          rootState.administration.contractors.list.selectedContractorId
        );
        commit.setAutoInvoiceSettings(result);
      }
    },
    async removeAutoInvoiceConfigurationId(context, param: number) {
      const { dispatch } = _moduleActionContext(context);
      await removeAutoInvoiceConfiguration(param);
      dispatch.downloadAutoInvoiceSetting();
    },

    async downloadServices(context) {
      const { state, commit } = _moduleActionContext(context);
      commit.setAreUnusedServicesLoading(true);
      const response = await getAllServiceTypesAutoInvoiceConfigurations(
        {
          count: state.serviceTypeDataTableOptions.itemsPerPage,
          page: state.serviceTypeDataTableOptions.page - 1,
          criterions: state.serviceTypesFilters,
          filterName: FILTER_NAMES.ServiceTypeSaleFilter,
        },
        state.autoInvoiceConfigurationId as number
      );
      commit.setServiceTypesCount(response.totalCount);
      commit.setUnusedService(response.items);
      commit.setAreUnusedServicesLoading(false);
    },
    async downloadSelectedServices(context) {
      const { state, commit } = _moduleActionContext(context);
      commit.setAreSelectedServicesLoading(true);
      commit.setSelectedService(
        await getServiceTypesAutoInvoiceConfigurations(state.autoInvoiceConfigurationId as number)
      );
      commit.setAreSelectedServicesLoading(false);
    },
    async downloadAllServices(context, filtered: boolean) {
      const { state, commit } = _moduleActionContext(context);
      commit.setAreSelectedServicesLoading(true);
      const response = await getFilteredServiceTypesAutoInvoiceConfigurations(
        {
          count: state.serviceTypeDataTableOptions.itemsPerPage,
          page: state.serviceTypeDataTableOptions.page - 1,
          criterions: filtered ? state.serviceTypesFilters : null,
          filterName: FILTER_NAMES.ServiceTypeSaleFilter,
        },
        state.autoInvoiceConfigurationId as number
      );
      if (filtered) {
        const tmp = state.selectedServices;
        const ids = state.selectedServices.map((v) => v.id);
        for (const item of response) {
          if (!ids.includes(item.id)) {
            tmp.push(item);
          }
        }
        commit.setSelectedService(tmp);
      } else commit.setSelectedService(response);
      commit.setAreSelectedServicesLoading(false);
    },
    async saveServices(context) {
      const { state } = _moduleActionContext(context);
      await editAutoInvoiceConfigurationServiceTypes(
        state.autoInvoiceConfigurationId as number,
        state.selectedServices.map((y) => y.id)
      );
    },
  },
});

export default AdministrationContractorAutoInvoiceModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorAutoInvoiceModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorAutoInvoiceModule);
